import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useTheme,
  Grid,
  CircularProgress,
} from "@mui/material";
import { ref, onValue } from "firebase/database"; // Import Firebase Realtime Database functions
import { db } from "./firebase"; // Import your initialized Firebase app and db
import SimplePageHeader from "./Components/Navigation/SimplePageHeader";

const KnowledgeBasePage = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [knowledgeBaseData, setKnowledgeBaseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from Realtime Database
  useEffect(() => {
    const knowledgeBaseRef = ref(db, "knowledgeBase"); // Reference to 'knowledgeBase' in Realtime Database
    onValue(
      knowledgeBaseRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // Transform the fetched data into an array
          const formattedData = Object.keys(data).map((key) => ({
            category: data[key].category,
            icon: data[key].icon, // The icon can be used as necessary
            items: Object.values(data[key].items),
          }));
          setKnowledgeBaseData(formattedData);
        }
        setLoading(false);
      },
      (errorObject) => {
        setError("Error fetching data");
        setLoading(false);
        console.error("The read failed: " + errorObject.name);
      }
    );
  }, []);

  // Filter knowledge base content based on the search query
  const filteredData = knowledgeBaseData
    .map((category) => ({
      ...category,
      items: category.items.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.items.length > 0); // Only include categories with matching items

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="h6" align="center" mt={4}>
        {error}
      </Typography>
    );
  }

  return (
    <>
      <SimplePageHeader
        iconName="kb"
        onSearchChange={setSearchQuery}
        titleString="Knowledge Base"
        placeholderText="Search Knowledge Base"
        showAddButton={false}
        pageType="kb"
        showDownloadButton={true}
      />

      {filteredData.length === 0 ? (
        <Typography variant="h6" align="center" mt={4}>
          No results found for "{searchQuery}"
        </Typography>
      ) : (
        <Grid container spacing={4} sx={{ paddingX: 4, marginTop: 1 }}>
          {filteredData.map((category, index) => (
            <Grid item xs={12} sm={12} md={6} lg={4} key={index}>
              <Box
                padding={2}
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                <i
                  className={`${category.icon.prefix} fa-${category.icon.iconName}`}
                  style={{
                    color: theme.palette.primary.main,
                    marginRight: theme.spacing(1),
                    fontSize: "1.5rem",
                  }}
                ></i>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.success.text,
                  }}
                >
                  {category.category}
                </Typography>
              </Box>

              {category.items.map((item, itemIndex) => (
                <Accordion
                  key={itemIndex}
                  sx={{
                    backgroundColor: theme.palette.success.main,
                    color: theme.palette.success.text,
                    mb: 2,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<i className="fas fa-expand"></i>}
                    aria-controls={`panel${itemIndex}-content`}
                    id={`panel${itemIndex}-header`}
                  >
                    <Typography>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>{item.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default KnowledgeBasePage;
